import { useEffect, useMemo } from 'react'
import { InboxOutlined } from '@ant-design/icons'
import { Alert, Button, Checkbox, Col, Form, Input, Row, Select, Upload } from 'antd'
import { Link } from 'react-router-dom'
import { fileToBase64 } from '@/lib/files'
import type { SignupCustomerForm } from '../../types'
import { checkCompanyExists, checkEmailExists } from '../../api'
import { AUTH_PRIMARY_BUTTON_CLASS } from '../../constants'
import { countryOptions, caProvinceOptions } from '../utils'
import { sanitizePhoneInput, normalizeCustomerForm } from '../utils'
import { checkAdvicePrepExists } from '@/features/adviceprep/api'
import { useSignup } from '../SignupContext'

const MAX_LOGO_FILE_SIZE_BYTES = 2 * 1024 * 1024

export default function ClientInfoStepForm() {
  const {
    customerInfoForm,
    customerForm,
    setCustomerForm,
    setCompanyExists,
    setEmailExists,
    setExistingCompanyUser,
    setShowBasExistsModal,
    setShowExistingUserModal,
    setStep,
    setError,
    setIsChildClient,
    runAction,
    createAccountWithoutPayment,
    isBasSignup,
    isAdvicePrepSignup,
    clientSignupCompleted,
    setAdvicePrepDuplicate,
    advicePrepDuplicate,
    openAdvicePrepConfirm,
    nocc,
    companyExists,
    emailExists,
    isCheckingConflicts,
    setIsCheckingConflicts,
    loading,
    hideBackButton,
  } = useSignup()

  const selectedCountry = Form.useWatch('code', customerInfoForm) || customerForm?.code || 'AU'
  const passwordValue = Form.useWatch('password', customerInfoForm) || ''
  const logoFileName = Form.useWatch('logoFileName', customerInfoForm) || ''

  const passwordStrength = useMemo(() => {
    const score =
      (passwordValue.length >= 8 ? 1 : 0) +
      (/[A-Z]/.test(passwordValue) ? 1 : 0) +
      (/[a-z]/.test(passwordValue) ? 1 : 0) +
      (/\d/.test(passwordValue) ? 1 : 0) +
      (/[^A-Za-z0-9]/.test(passwordValue) ? 1 : 0)

    if (!passwordValue) return { label: '', activeBars: 0 }
    if (score <= 2) return { label: 'Weak', activeBars: 1 }
    if (score <= 4) return { label: 'Medium', activeBars: 3 }
    return { label: 'Strong', activeBars: 4 }
  }, [passwordValue])

  useEffect(() => {
    if (customerForm) {
      customerInfoForm.setFieldsValue(customerForm)
    }
  }, [customerForm, customerInfoForm])

  useEffect(() => {
    const code = customerInfoForm.getFieldValue('code')
    if (code !== 'CA') customerInfoForm.setFieldValue('state', undefined)
    if (code !== 'US') customerInfoForm.setFieldValue('postalCode', undefined)
  }, [customerInfoForm, selectedCountry])

  const checkConflicts = async (formValues: SignupCustomerForm) => {
    const [company, email] = await Promise.all([
      checkCompanyExists({
        Email: formValues.email.trim().toLowerCase(),
        Company: formValues.companyName.trim(),
        isBas: isBasSignup,
      }),
      checkEmailExists({
        Email: formValues.email.trim().toLowerCase(),
        Company: formValues.companyName.trim(),
        isBas: isBasSignup,
      }),
    ])
    setCompanyExists(company)
    setEmailExists(email.exist)
    setExistingCompanyUser(email.existingCompanyUser || '')
    return { company, emailExists: email.exist }
  }

  const validateConflictsOnBlur = async () => {
    try {
      const values = customerInfoForm.getFieldsValue()
      if (!values.email?.trim() || !values.companyName?.trim()) return
      setIsCheckingConflicts(true)
      const normalized = normalizeCustomerForm(values as SignupCustomerForm)

      if (isAdvicePrepSignup) {
        const exists = await checkAdvicePrepExists({
          email: normalized.email,
          companyName: normalized.companyName,
        })
        setAdvicePrepDuplicate(exists.emailExists || exists.companyExists)
        return
      }

      await checkConflicts(normalized)
    } catch {
      //
    } finally {
      setIsCheckingConflicts(false)
    }
  }

  const handleNext = async () => {
    await runAction(async () => {
      const values = normalizeCustomerForm(await customerInfoForm.validateFields())

      // AdvicePrep never creates a child client, so Next runs its own duplicate
      // check and either hard-stops or opens the confirmation modal. Nothing is
      // saved until that modal is confirmed (D-5).
      if (isAdvicePrepSignup) {
        // Coming Back from Pricing, the account already exists — so the
        // duplicate check would find it and hard-stop the user inside their own
        // signup, with no way forward or out.
        if (clientSignupCompleted && customerForm?.clientId) {
          setStep('pricing')
          return
        }

        setAdvicePrepDuplicate(false)
        // A pre-check that cannot run must not block signup — 5.1c re-checks
        // uniqueness server-side and is the actual authority.
        try {
          const exists = await checkAdvicePrepExists({ email: values.email, companyName: values.companyName })
          if (exists.emailExists || exists.companyExists) {
            setAdvicePrepDuplicate(true)
            return
          }
        } catch {
          //
        }
        openAdvicePrepConfirm(values)
        return
      }

      const conflict = await checkConflicts(values)

      if (isBasSignup && conflict.emailExists) {
        setShowBasExistsModal(true)
        return
      }
      if (conflict.emailExists && !isBasSignup) {
        setShowExistingUserModal(true)
        setCustomerForm(normalizeCustomerForm(values, true))
        return
      }
      if (conflict.company) {
        setError('Company already exists.')
        return
      }

      const payload = normalizeCustomerForm(values)
      setCustomerForm(payload)
      setIsChildClient(false)

      if (nocc) {
        await createAccountWithoutPayment(payload, false)
        setStep('importData')
        return
      }
      setStep('pricing')
    })
  }

  const handleClear = () => {
    customerInfoForm.resetFields([
      'firstName',
      'lastName',
      'email',
      'password',
      'companyName',
      'companyshortName',
      'phone',
      'state',
      'postalCode',
      'base64image',
      'contentType',
      'logoFileName',
      'isaccepted',
    ])
  }

  const handleBack = () => setStep('product')

  const clearLogoFields = () => {
    customerInfoForm.setFieldValue('base64image', undefined)
    customerInfoForm.setFieldValue('contentType', undefined)
    customerInfoForm.setFieldValue('logoFileName', undefined)
    customerInfoForm.setFields([{ name: 'base64image', errors: [] }])
  }

  const processLogoFile = (file: File) => {
    if (file.size > MAX_LOGO_FILE_SIZE_BYTES) {
      clearLogoFields()
      customerInfoForm.setFields([
        { name: 'base64image', errors: ['Logo file is too large. Please upload a file smaller than 2MB.'] },
      ])
      return Upload.LIST_IGNORE
    }

    customerInfoForm.setFieldValue('logoFileName', file.name)
    customerInfoForm.setFieldValue('contentType', file.type)
    customerInfoForm.setFields([{ name: 'base64image', errors: [] }])
    void fileToBase64(file)
      .then((base64) => {
        customerInfoForm.setFieldValue('base64image', base64)
      })
      .catch(() => {
        customerInfoForm.setFieldValue('base64image', undefined)
        customerInfoForm.setFieldValue('contentType', undefined)
        customerInfoForm.setFields([
          { name: 'base64image', errors: ['Unable to read the selected logo file. Please try again.'] },
        ])
      })
    return false
  }

  return (
    <>
      {advicePrepDuplicate && (
        <Alert
          type="error"
          showIcon
          className="mb-3"
          message="You already have a Dashboard Insights account"
          description={
            <>
              Don&apos;t sign up again —{' '}
              <Link to="/login" className="font-medium text-brand-600 underline hover:text-brand-700">
                log in to your existing account
              </Link>{' '}
              and add AdvicePrep from there. It goes onto your current subscription and billing, and your
              login stays the same.
            </>
          }
        />
      )}
      <Form
        form={customerInfoForm}
        layout="vertical"
        initialValues={customerForm || { code: 'AU', isaccepted: false }}
        className="w-full"
      >
        <div className="signup-form-section-title">Personal Information</div>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="firstName" label="First Name" rules={[{ required: true }, { min: 3 }, { max: 100 }]} hasFeedback>
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="lastName" label="Last Name" rules={[{ required: true }, { min: 3 }, { max: 100 }]} hasFeedback>
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[{ required: true }, { type: 'email' }, { min: 3 }, { max: 360 }]}
              hasFeedback
            >
              <Input autoComplete="email" onBlur={() => void validateConflictsOnBlur()} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="password" label="Password" rules={[{ required: true }, { min: 8 }, { max: 100 }]} hasFeedback>
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            {passwordStrength.label ? (
              <div className="signup-password-strength" aria-live="polite">
                <div className="signup-password-strength-bars">
                  {[0, 1, 2, 3].map((index) => (
                    <span
                      key={index}
                      className={index < passwordStrength.activeBars ? 'is-active' : ''}
                    />
                  ))}
                </div>
                <div className="signup-password-strength-label">{passwordStrength.label}</div>
              </div>
            ) : null}
          </Col>
        </Row>
        <div className="signup-form-section-title">Company Information</div>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="companyName" label="Company Name" rules={[{ required: true }, { min: 3 }, { max: 100 }]} hasFeedback>
              <Input onBlur={() => void validateConflictsOnBlur()} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="companyshortName"
              label="Company Short Name"
              rules={[{ required: true }, { min: 3 }, { max: 100 }]}
              hasFeedback
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <div className="signup-form-section-title">Contact</div>
        <Form.Item label="Phone number" className="signup-grid-span-2">
          <div className="signup-contact-row">
            <Form.Item name="code" rules={[{ required: true }]} className="signup-contact-code">
              <Select options={countryOptions} />
            </Form.Item>
            <Form.Item
              name="phone"
              rules={[
                { required: true },
                { min: 8 },
                { max: 14 },
                { pattern: /^[0-9]+$/, message: 'Phone should only have numeric symbols' },
              ]}
              className="signup-contact-phone"
            >
              <Input
                placeholder="Phone number"
                maxLength={8}
                onChange={(e) => {
                  const s = sanitizePhoneInput(e.target.value).replace(/\D/g, '').slice(0, 8)
                  if (s !== e.target.value) customerInfoForm.setFieldValue('phone', s)
                }}
              />
            </Form.Item>
          </div>
        </Form.Item>

        {selectedCountry === 'CA' && (
          <Form.Item name="state" label="State" rules={[{ required: true }]}>
            <Select options={caProvinceOptions} />
          </Form.Item>
        )}

        {selectedCountry === 'US' && (
          <Form.Item name="postalCode" label="Postal Code" rules={[{ required: true }, { min: 5 }]}>
            <Input />
          </Form.Item>
        )}

        <div className="signup-form-section-title">Logo Upload</div>
        <Form.Item name="logoFileName" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="contentType" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="base64image" label="Logo (optional)" className="signup-grid-span-2">
          <Upload.Dragger
            accept="image/*"
            maxCount={1}
            showUploadList={false}
            className="signup-logo-uploader"
            onRemove={() => clearLogoFields()}
            beforeUpload={(file: File) => {
              return processLogoFile(file)
            }}
            onChange={({ file }) => {
              const selectedFile = file.originFileObj
              if (selectedFile instanceof File) {
                void processLogoFile(selectedFile)
              }
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="signup-logo-uploader-title">Drag & drop logo here</p>
            <p className="signup-logo-uploader-subtitle">or <span>Browse File</span></p>
            <p className="signup-logo-uploader-hint">PNG, JPG - Max 2MB</p>
          </Upload.Dragger>
          {logoFileName ? (
            <div className="signup-logo-file-status">
              <span className="signup-logo-file-name">{logoFileName}</span>
              <Button
                type="link"
                className="signup-logo-file-remove"
                onClick={() => clearLogoFields()}
              >
                Remove
              </Button>
            </div>
          ) : null}
        </Form.Item>

        <Form.Item
          name="isaccepted"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) => (
                value
                  ? Promise.resolve()
                  : Promise.reject(new Error('Please agree to the Terms & Agreement to proceed'))
              ),
            },
          ]}
          className="signup-grid-span-2"
        >
          <Checkbox>
            I agree to the{' '}
            <a href="/assets/auth/files/Dashboard-Insights-Terms-and-Conditions.pdf" target="_blank" rel="noreferrer">
              Terms & Conditions
            </a>
          </Checkbox>
        </Form.Item>

        {(companyExists || emailExists) && (
          <Alert
            type="warning"
            showIcon
            message={companyExists ? 'Company already exists.' : 'Email already exists.'}
            className="mb-2"
          />
        )}
        {isCheckingConflicts && <div className="text-xs text-gray-500">Checking company/email availability...</div>}
      </Form>
      <div className="signup-step-actions">
        <Button onClick={handleClear} disabled={loading}>
          Clear
        </Button>
        {!hideBackButton && <Button onClick={handleBack} disabled={loading}>Back</Button>}
        <Button type="primary" loading={loading} disabled={loading} className={AUTH_PRIMARY_BUTTON_CLASS} onClick={() => void handleNext()}>
          Next
        </Button>
      </div>
    </>
  )
}
